import { createAsyncThunk } from '@reduxjs/toolkit';
import tableService from '../../services/tableService';
import orderService from '../../services/orderService';
import localDB from '../../services/localDB';
import { flushPendingDeletes } from './orderThunks';
import { flushPendingExpenses } from './expenseThunks';
import { setTables } from './tablesSlice';
import { setOrders, upsertOrders } from './ordersSlice';

export const loadInitialPOSData = createAsyncThunk(
	'pos/loadInitialPOSData',
	async ({ branchId }, { dispatch }) => {
		try {
			// Flush any order deletes that were queued while offline so they don't
			// reappear in the active-orders list after reconnection.
			await flushPendingDeletes().catch(() => {});
			// Offline-created expenses too — this boot path matters because the
			// sync modal only auto-opens when ORDERS are pending; a queue holding
			// only expenses would otherwise never flush.
			flushPendingExpenses(branchId).catch(() => {});

			// Menu items are intentionally NOT fetched here — they can be very large
			// (base64 images per item) and POSScreen.loadBranchData() loads them with
			// proper restaurantId + branchId params via the POS controller.
			// Fetching them here with branchId-only caused 500s for large menus.
			const [tables, orders] = await Promise.all([
				tableService.getTablesByBranch(branchId),
				orderService.getActiveOrders(branchId),
			]);

			dispatch(setTables(tables || []));
			dispatch(setOrders(orders || []));

			// Cache the table layout so an offline boot can still render the
			// floor. Ayder 2026-07-15: LAN was down at open, this thunk failed,
			// and the Dining screen came up with no tables at all.
			if (Array.isArray(tables) && tables.length > 0) {
				localDB.setMeta(`tables_cache_${branchId}`, tables).catch(() => {});
			}

			// Merge any locally-buffered orders that haven't synced yet.
			// When online, filter out:
			//   1. Draft orders — transient UI placeholders that are orphaned in IndexedDB
			//      after the user checks out (checkout creates a real local_ order but the
			//      old draft_ entry stays in IndexedDB).
			//   2. Terminal-status orders (completed/billed/cancelled) — these belong in
			//      order history, not the active view; they will be synced and then the
			//      server's reconciled state will replace Redux on sync completion.
			//      EXCEPTION: an _isLocal (un-synced) row is kept regardless of status —
			//      e.g. a pending billed→modify→re-bill edit. getActiveOrders excludes
			//      billed, so setOrders above already dropped it; without this a full
			//      branch reload before the pump settles would wipe the pending
			//      modification from Redux (consistency with fetchActiveOrders /
			//      syncRunner, which both preserve on _isLocal regardless of status).
			const localOrders = await localDB.getAllOrders(branchId).catch(() => []);
			const activeLocalOrders = localOrders.filter(o =>
				!o.isDraft &&
				o.status !== 'draft' &&
				(o._isLocal === true || !['completed', 'billed', 'cancelled'].includes(o.status))
			);
			if (activeLocalOrders.length > 0) {
				dispatch(upsertOrders(activeLocalOrders));
			}

			return {
				tableCount: tables?.length     || 0,
				orderCount: (orders?.length || 0) + activeLocalOrders.length,
			};
		} catch (error) {
			// API unreachable — fall back to cached tables + local orders so the
			// counter still boots with a usable floor instead of a blank screen.
			const cachedTables = await localDB.getMeta(`tables_cache_${branchId}`).catch(() => null);
			if (Array.isArray(cachedTables) && cachedTables.length > 0) {
				console.warn('[offline] boot: server unreachable — using cached tables', {
					branchId, tableCount: cachedTables.length,
				});
				dispatch(setTables(cachedTables));
			}
			const localOrders = await localDB.getAllOrders(branchId).catch(() => []);
			dispatch(setOrders(localOrders || []));
			return { menuCount: 0, tableCount: (cachedTables?.length || 0), orderCount: localOrders.length };
		}
	}
);
